(() => {
  const page = document.querySelector('[data-why-us-page]');

  if (!page) {
    return;
  }

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const revealItems = [...page.querySelectorAll('[data-why-reveal]')];

  if (reduceMotion || !('IntersectionObserver' in window)) {
    revealItems.forEach((item) => item.classList.add('is-visible'));
  } else {
    page.setAttribute('data-why-motion-ready', '');

    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.16,
      }
    );

    revealItems.forEach((item) => revealObserver.observe(item));
  }

  const hero = page.querySelector('[data-why-hero]');
  const heroMedia = page.querySelector('[data-why-hero-media]');
  let ticking = false;

  const updateHeroShift = () => {
    ticking = false;

    if (!hero || !heroMedia || reduceMotion) {
      return;
    }

    const rect = hero.getBoundingClientRect();

    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      return;
    }

    const progress = Math.min(1, Math.max(0, -rect.top / Math.max(rect.height, 1)));
    heroMedia.style.setProperty('--why-us-hero-shift', `${progress * 18}px`);
  };

  const requestHeroShift = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateHeroShift);
      ticking = true;
    }
  };

  updateHeroShift();
  window.addEventListener('scroll', requestHeroShift, { passive: true });
  window.addEventListener('resize', requestHeroShift);

  const sectionNav = page.querySelector('[data-why-section-nav]');

  if (sectionNav) {
    const navButtons = [...sectionNav.querySelectorAll('[data-why-nav-target]')];
    const sectionAnchors = [...page.querySelectorAll('[data-why-section-anchor]')];

    const setActiveNav = (sectionName) => {
      navButtons.forEach((button) => {
        const isActive = button.dataset.whyNavTarget === sectionName;
        button.classList.toggle('is-active', isActive);

        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    };

    navButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const target = page.querySelector(`[data-why-section-anchor="${button.dataset.whyNavTarget}"]`);

        if (target) {
          target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        }
      });
    });

    if ('IntersectionObserver' in window && sectionAnchors.length > 0) {
      const navObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveNav(entry.target.dataset.whySectionAnchor);
            }
          });
        },
        {
          rootMargin: '-42% 0px -42% 0px',
          threshold: 0,
        }
      );

      sectionAnchors.forEach((section) => navObserver.observe(section));
    }
  }

  page.querySelectorAll('[data-why-spotlight]').forEach((item) => {
    item.addEventListener('pointermove', (event) => {
      const rect = item.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;

      item.style.setProperty('--why-us-spotlight-x', `${x}%`);
      item.style.setProperty('--why-us-spotlight-y', `${y}%`);
    });

    item.addEventListener('pointerleave', () => {
      item.style.removeProperty('--why-us-spotlight-x');
      item.style.removeProperty('--why-us-spotlight-y');
    });
  });

  const animateCounter = (counter) => {
    if (counter.dataset.countAnimated === 'true') {
      return;
    }

    counter.dataset.countAnimated = 'true';

    const target = Number(counter.dataset.countTarget || 0);
    const prefix = counter.dataset.countPrefix || '';
    const suffix = counter.dataset.countSuffix || '';

    if (reduceMotion || target <= 0) {
      counter.textContent = `${prefix}${target}${suffix}`;
      return;
    }

    const duration = 900;
    const startedAt = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(target * eased);

      counter.textContent = `${prefix}${current}${suffix}`;

      if (progress < 1) {
        window.requestAnimationFrame(tick);
      }
    };

    window.requestAnimationFrame(tick);
  };

  const counters = [...page.querySelectorAll('[data-why-count]')];

  if (reduceMotion || !('IntersectionObserver' in window)) {
    counters.forEach(animateCounter);
  } else {
    const counterObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCounter(entry.target);
            counterObserver.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((counter) => counterObserver.observe(counter));
  }

  const compareSlider = page.querySelector('[data-why-compare-slider]');

  if (compareSlider) {
    const track = compareSlider.querySelector('[data-why-compare-track]');
    const cards = [...compareSlider.querySelectorAll('[data-why-compare-card]')];
    const dots = [...compareSlider.querySelectorAll('[data-why-compare-dot]')];
    const currentLabel = compareSlider.querySelector('[data-why-compare-current]');
    const progress = compareSlider.querySelector('[data-why-compare-progress]');
    const prevButton = compareSlider.querySelector('[data-why-compare-prev]');
    const nextButton = compareSlider.querySelector('[data-why-compare-next]');
    const viewport = compareSlider.querySelector('.why-us-compare-slider__viewport');
    let activeCompareIndex = 0;
    let startX = null;

    const setCompareSlide = (index) => {
      if (!track || cards.length === 0) {
        return;
      }

      activeCompareIndex = (index + cards.length) % cards.length;
      track.style.transform = `translateX(-${activeCompareIndex * 100}%)`;

      if (currentLabel) {
        currentLabel.textContent = String(activeCompareIndex + 1).padStart(2, '0');
      }

      if (progress) {
        progress.style.transform = `scaleX(${(activeCompareIndex + 1) / cards.length})`;
      }

      cards.forEach((card, cardIndex) => {
        const isActive = cardIndex === activeCompareIndex;
        card.classList.toggle('is-active', isActive);
        card.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle('is-active', dotIndex === activeCompareIndex);
      });
    };

    prevButton?.addEventListener('click', () => setCompareSlide(activeCompareIndex - 1));
    nextButton?.addEventListener('click', () => setCompareSlide(activeCompareIndex + 1));

    dots.forEach((dot, dotIndex) => {
      dot.addEventListener('click', () => setCompareSlide(dotIndex));
    });

    compareSlider.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        return;
      }

      event.preventDefault();
      setCompareSlide(activeCompareIndex + (event.key === 'ArrowRight' ? 1 : -1));
    });

    viewport?.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      viewport.setPointerCapture?.(event.pointerId);
    });

    viewport?.addEventListener('pointerup', (event) => {
      if (startX === null) {
        return;
      }

      const distance = event.clientX - startX;
      startX = null;

      if (Math.abs(distance) > 38) {
        setCompareSlide(activeCompareIndex + (distance < 0 ? 1 : -1));
      }
    });

    setCompareSlide(0);
  }

  const pathway = page.querySelector('[data-why-pathway]');

  if (!pathway) {
    return;
  }

  const steps = [...pathway.querySelectorAll('.why-us-pathway__step')];
  const panelIndex = pathway.querySelector('[data-pathway-panel-index]');
  const panelTitle = pathway.querySelector('[data-pathway-panel-title]');
  const panelText = pathway.querySelector('[data-pathway-panel-text]');
  const panel = pathway.querySelector('[data-why-pathway-panel]');
  const pathwayPrev = pathway.querySelector('[data-pathway-prev]');
  const pathwayNext = pathway.querySelector('[data-pathway-next]');
  const pathwayProgress = pathway.querySelector('[data-pathway-progress]');
  const pathwayImages = [...pathway.querySelectorAll('[data-pathway-image]')];
  let activePathwayIndex = 0;
  let pathwayStartX = null;
  const interactivePathwaySelector = 'a, button, input, label, select, textarea';

  const activateStep = (step) => {
    const stepIndex = steps.indexOf(step);

    if (stepIndex >= 0) {
      activePathwayIndex = stepIndex;
    }

    if (panel && !reduceMotion) {
      panel.classList.remove('is-changing');
      panel.offsetWidth;
      panel.classList.add('is-changing');
      window.setTimeout(() => panel.classList.remove('is-changing'), 180);
    }

    steps.forEach((item) => {
      item.classList.toggle('is-active', item === step);

      if (item === step) {
        item.setAttribute('aria-current', 'step');
      } else {
        item.removeAttribute('aria-current');
      }
    });

    if (panelIndex) {
      panelIndex.textContent = step.dataset.pathwayIndex || '';
    }

    if (pathwayProgress) {
      pathwayProgress.style.transform = `scaleX(${(activePathwayIndex + 1) / steps.length})`;
    }

    if (pathwayImages.length > 0) {
      const activeImage =
        pathwayImages.find((image) => Number(image.dataset.pathwayImage) === activePathwayIndex) ||
        pathwayImages[activePathwayIndex % pathwayImages.length];

      pathwayImages.forEach((image) => {
        image.classList.toggle('is-active', image === activeImage);
      });
    }

    if (panelTitle) {
      panelTitle.textContent = step.dataset.pathwayTitle || '';
    }

    if (panelText) {
      panelText.textContent = step.dataset.pathwayText || '';
    }
  };

  const activateStepByIndex = (index) => {
    if (steps.length === 0) {
      return;
    }

    const nextIndex = (index + steps.length) % steps.length;
    activateStep(steps[nextIndex]);
  };

  steps.forEach((step, index) => {
    step.addEventListener('click', () => activateStep(step));

    step.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
        return;
      }

      event.preventDefault();

      const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      const nextIndex = (index + direction + steps.length) % steps.length;

      activateStepByIndex(nextIndex);
      steps[nextIndex].focus();
    });
  });

  pathwayPrev?.addEventListener('click', () => activateStepByIndex(activePathwayIndex - 1));
  pathwayNext?.addEventListener('click', () => activateStepByIndex(activePathwayIndex + 1));

  panel?.addEventListener('pointerdown', (event) => {
    if (event.target.closest(interactivePathwaySelector)) {
      pathwayStartX = null;
      return;
    }

    pathwayStartX = event.clientX;
    panel.setPointerCapture?.(event.pointerId);
  });

  panel?.addEventListener('pointerup', (event) => {
    if (pathwayStartX === null) {
      return;
    }

    const distance = event.clientX - pathwayStartX;
    pathwayStartX = null;

    if (Math.abs(distance) > 38) {
      activateStepByIndex(activePathwayIndex + (distance < 0 ? 1 : -1));
    }
  });

  activateStepByIndex(0);
})();
